import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { PropTypes } from 'prop-types';

import '../common.scss';
import './DateRangePicker.scss';

export default function CsrmDateRangePicker({
  dateRange,
  onDateRangeChange,
  showOutline,
  showLabels,
  useToSeperator,
}) {
  const [startDateInput, setStartDateInput] = useState(
    dateRange[0] || new Date(),
  );
  const [endDateInput, setEndDateInput] = useState(dateRange[1] || new Date());
  const [startDateInputIsError, setStartDateInputIsError] = useState(false);
  const [endDateInputIsError, setEndDateInputIsError] = useState(false);

  // TODO: how should min/max dates be set?
  const minDate = moment('01/01/1950', 'MM/dd/yyyy').startOf('d').toDate();
  const maxDate = moment().startOf('d').add(1, 'd').toDate();

  const isInputDateValid = (date) => !date || date < minDate || date > maxDate || !moment(date).isValid();

  useEffect(() => {
    const [startDate, endDate] = dateRange || [];
    const testInput = new Date(startDateInput || minDate);
    const isError = isInputDateValid(testInput);
    setStartDateInputIsError(isError);
    if (!isError && testInput !== startDate) {
      onDateRangeChange([testInput, endDate]);
      if (!endDateInputIsError) setEndDateInputIsError(testInput > endDate);
    }
  }, [startDateInput]);

  useEffect(() => {
    const [startDate, endDate] = dateRange || [];
    const testInput = new Date(endDateInput || maxDate);
    const isError = isInputDateValid(testInput);
    setEndDateInputIsError(isError);
    if (!isError && testInput !== endDate) {
      onDateRangeChange([startDate, testInput]);
      if (!startDateInputIsError) { setStartDateInputIsError(testInput < startDate); }
    }
  }, [endDateInput]);

  const dateFormat = 'MM/DD/yyyy';
  useEffect(() => {
    const [startDate, endDate] = dateRange || [];
    setStartDateInput(moment(startDate).format(dateFormat));
    setEndDateInput(moment(endDate).format(dateFormat));
  }, [dateRange]);

  return (
    <div className="csrm-date-range-picker">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label={showLabels ? 'Start Date' : ''}
          variant="outlined"
          mask="__/__/____"
          showTodayButton
          allowSameDateSelection
          toolbarTitle="Choose a date"
          inputFormat="MM/dd/yyyy"
          error={startDateInputIsError}
          value={startDateInput}
          onChange={setStartDateInput}
          minDate={minDate}
          maxDate={dateRange[1]}
          renderInput={(params) => (
            <TextField
              className={`date-picker-input ${showOutline ? '' : 'no-outline'}`}
              {...params}
            />
          )}
        />
        {useToSeperator ? (
          <span style={{ textTransform: 'lowercase' }}>&nbsp;to&nbsp;</span>
        ) : (
          ''
        )}

        <DatePicker
          label={showLabels ? 'End Date' : ''}
          variant="outlined"
          mask="__/__/____"
          showTodayButton
          allowSameDateSelection
          toolbarTitle="Choose a date"
          inputFormat="MM/dd/yyyy"
          error={endDateInputIsError}
          value={endDateInput}
          onChange={setEndDateInput}
          minDate={dateRange[0]}
          maxDate={maxDate}
          renderInput={(params) => (
            <TextField
              className={`date-picker-input ${showOutline ? '' : 'no-outline'}`}
              {...params}
            />
          )}
        />
      </LocalizationProvider>
    </div>
  );
}

CsrmDateRangePicker.propTypes = {
  dateRange: PropTypes.arrayOf(PropTypes.object).isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  showOutline: PropTypes.bool,
  showLabels: PropTypes.bool,
  useToSeperator: PropTypes.bool,
};

CsrmDateRangePicker.defaultProps = {
  showOutline: false,
  showLabels: false,
  useToSeperator: false,
};
